<template>
  <mdb-container fluid class="member-lookup">
    <section class="lookup-header">
      <div>
        <h2>{{ $t("member_lookup_title") }}</h2>
        <p>{{ $t("surrogate_tel_input_title") }}</p>
      </div>
      <div class="lookup-form">
        <mdb-input
          v-model.trim="surrogatetel"
          icon="search"
          :label="$t('surrogate_tel_input')"
          type="text"
          @keyup.enter.native="startFindingMemberIdDataWithCode"
        />
        <mdb-btn color="indigo" :disabled="!surrogatetel" @click="startFindingMemberIdDataWithCode">
          <mdb-icon icon="search" class="mr-1" />{{ $t("search") }}
        </mdb-btn>
      </div>
    </section>

    <div v-if="dataLoadReady" class="lookup-results">
      <div v-if="customerIdMismatch" class="status-message status-message--danger">
        <mdb-icon icon="exclamation-triangle" />
        <strong>{{ $t("customer_mismatch") }}</strong>
      </div>

      <div class="result-grid">
        <section class="result-panel">
          <header><mdb-icon icon="user" />{{ $t("customer_data") }}</header>
          <dl v-if="hasCustomer">
            <div><dt>{{ $t("customer_id") }}</dt><dd>{{ customer.id }}</dd></div>
            <div><dt>{{ $t("member_id") }}</dt><dd>{{ displayValue(customer.surrogate) }}</dd></div>
            <div><dt>{{ $t("phone_number") }}</dt><dd>{{ displayValue(customer.tel) }}</dd></div>
            <div><dt>{{ $t("email") }}</dt><dd>{{ displayValue(customer.email) }}</dd></div>
            <div><dt>Mobile ID</dt><dd>{{ displayValue(customer.mobileloginid) }}</dd></div>
            <div><dt>{{ $t("created") }}</dt><dd>{{ displayValue(customer.created) }}</dd></div>
            <div><dt>{{ $t("updated") }}</dt><dd>{{ displayValue(customer.updated) }}</dd></div>
          </dl>
          <p v-else class="empty-result">{{ $t("no_customer_found") }}</p>
        </section>

        <section class="result-panel">
          <header><mdb-icon icon="id-card" />{{ $t("member_id") }}</header>
          <dl v-if="hasSurrogate">
            <div><dt>{{ $t("member_id") }}</dt><dd>{{ surrogate.surrogate }}</dd></div>
            <div>
              <dt>{{ $t("customer_id") }}</dt>
              <dd :class="{ 'empty-status': !surrogate.customer_id }">
                {{ surrogate.customer_id || $t("not_in_use") }}
              </dd>
            </div>
            <div><dt>{{ $t("name") }}</dt><dd>{{ displayValue(surrogate.name) }}</dd></div>
            <div><dt>{{ $t("valid_until") }}</dt><dd>{{ displayValue(surrogate.lastuntil) }}</dd></div>
            <div>
              <dt>{{ $t("activated") }}</dt>
              <dd :class="{ 'empty-status': !surrogate.active }">
                {{ surrogate.active || $t("not_activated") }}
              </dd>
            </div>
            <div><dt>{{ $t("updated") }}</dt><dd>{{ displayValue(surrogate.updated) }}</dd></div>
          </dl>
          <p v-else class="empty-result">{{ $t("no_member_id_found") }}</p>
        </section>

        <section class="result-panel result-panel--passwords">
          <header>
            <mdb-icon icon="key" />{{ $t("one_time_passwords") }}
            <span class="result-count">{{ passwords.length }}</span>
          </header>
          <div v-if="passwords.length" class="password-list">
            <article v-for="password in passwords" :key="password.id" class="password-item">
              <div class="password-code">
                <strong>{{ password.password }}</strong>
                <span :class="['password-status', password.invalidated ? 'password-status--danger' : 'password-status--ok']">
                  {{ password.invalidated ? $t("invalidated") : $t("valid") }}
                </span>
              </div>
              <dl>
                <div><dt>{{ $t("name") }}</dt><dd>{{ displayValue(password.name) }}</dd></div>
                <div><dt>{{ $t("phone_number") }}</dt><dd>{{ displayValue(password.tel) }}</dd></div>
                <div><dt>{{ $t("customer_id") }}</dt><dd>{{ displayValue(password.customer_id) }}</dd></div>
                <div><dt>{{ $t("valid_until") }}</dt><dd>{{ displayValue(password.validto) }}</dd></div>
                <div><dt>{{ $t("used") }}</dt><dd>{{ displayValue(password.used) }}</dd></div>
              </dl>
            </article>
          </div>
          <p v-else class="empty-result">{{ $t("no_one_time_password_found") }}</p>
        </section>
      </div>

      <footer v-if="buttonsVisible" class="lookup-actions">
        <mdb-btn color="green" @click="changeSurrogateToCustomer">
          {{ $t("change_member_id_to_customer") }}
        </mdb-btn>
        <mdb-btn color="orange" @click="cleanSurrogate">{{ $t("clean_member_id") }}</mdb-btn>
        <mdb-btn color="warning" :disabled="!passwords.length" @click="cleanPasswords">
          {{ $t("clean_one_time_password") }}
        </mdb-btn>
        <mdb-btn color="red" @click="deleteMemberIdData">{{ $t("delete_customer_and_clean_access") }}</mdb-btn>
      </footer>
    </div>
  </mdb-container>
</template>

<script>
import { mdbBtn, mdbContainer, mdbIcon, mdbInput } from "mdbvue";
export default {
  name: "FindMemberIdsTogetherWithCustomer",
  components: {
    mdbBtn,
    mdbContainer,
    mdbIcon,
    mdbInput,
  },
  data() {
    return {
      surrogatetel: "",
      dataLoadReady: false,
      buttonsVisible: false,
      foundSurrogate: null,
      foundCustomerId: null,
      customerIdMismatch: false,
      customer: {},
      surrogate: {},
      passwords: [],
      changecustomersurrogatepath: "/1.0/front/customer/surrogatetocustomer/change",
      deleteandcleancustomerpath: "/1.0/front/customer/surrogate-password/delete",
      cleansurrogatepath: "/1.0/front/customer/surrogate/clean",
      cleanpasswordpath: "/1.0/front/customer/password/clean",
      retrievebypath: "/1.0/front/customer/retrieve/surrogatewithcustomer/",
    };
  },
  computed: {
    hasCustomer() {
      return Boolean(this.customer.id);
    },
    hasSurrogate() {
      return Boolean(this.surrogate.surrogate);
    },
  },
  methods: {
    displayValue(value) {
      return value === null || value === undefined || value === "" ? "-" : value;
    },
    startFindingMemberIdDataWithCode() {
      this.dataLoadReady = false;
      this.buttonsVisible = false;
      this.findMemberIdDataWithCode();
    },
    cleanSurrogate() {
      this.$http
        .post(this.cleansurrogatepath, { surrogate: this.foundSurrogate })
        .then((response) => {
          if (response.status == 200) {
            this.startFindingMemberIdDataWithCode();
            this.$notify.info({
              message: this.$t("member_id_cleaned"),
              position: "top right",
              timeOut: this.$store.state.notifytimeoutmedium,
            });
          } else if (response.status == 401) {
            this.$root.$emit("session_failed");
          }
        })
        .catch((error) => {
          return error;
        });
    },
    cleanPasswords() {
      this.$http
        .post(this.cleanpasswordpath, {
          customer_id: this.foundCustomerId,
          password_ids: this.passwords.map((password) => password.id),
          tel: this.searchPhoneNumber(),
        })
        .then((response) => {
          if (response.status == 200) {
            this.startFindingMemberIdDataWithCode();
            this.$notify.info({
              message: this.$t("one_time_password_cleaned"),
              position: "top right",
              timeOut: this.$store.state.notifytimeoutmedium,
            });
          } else if (response.status == 401) {
            this.$root.$emit("session_failed");
          }
        })
        .catch((error) => {
          return error;
        });
    },
    changeSurrogateToCustomer() {
      this.$http
        .post(this.changecustomersurrogatepath, { surrogate: this.foundSurrogate, customer_id: this.foundCustomerId })
        .then((response) => {
          if (response.status == 200) {
            this.startFindingMemberIdDataWithCode();
            this.$notify.info({
              message: this.$t("member_id_changed_to_customer"),
              position: "top right",
              timeOut: this.$store.state.notifytimeoutmedium,
            });
          } else if (response.status == 401) {
            this.$root.$emit("session_failed");
          }
        })
        .catch((error) => {
          return error;
        });
    },
    deleteMemberIdData() {
      if (!confirm(this.$t("confirm_delete_customer_and_clean_access"))) {
        return;
      }
      this.$http
        .post(this.deleteandcleancustomerpath, {
          surrogate: this.foundSurrogate,
          customer_id: this.foundCustomerId,
          password_ids: this.passwords.map((password) => password.id),
          tel: this.searchPhoneNumber(),
        })
        .then((response) => {
          if (response.status == 200) {
            this.startFindingMemberIdDataWithCode();
            this.$notify.info({
              message: this.$t("member_id_deleted"),
              position: "top right",
              timeOut: this.$store.state.notifytimeoutmedium,
            });
          } else if (response.status == 401) {
            this.$root.$emit("session_failed");
          }
        })
        .catch((error) => {
          return error;
        });
    },
    findMemberIdDataWithCode() {
      this.$http
        .get(this.retrievebypath + this.surrogatetel)
        .then((response) => {
          if (response.status == 200) {
            this.customer = response.data.customer[0] || {};
            this.surrogate = response.data.surrogate[0] || {};
            this.passwords = response.data.passwords || [];
            this.foundCustomerId = this.customer.id || null;
            this.foundSurrogate = this.surrogate.surrogate || null;
            this.customerIdMismatch = Boolean(
              this.foundCustomerId && this.surrogate.customer_id && this.foundCustomerId != this.surrogate.customer_id
            );

            this.dataLoadReady = true;
            this.buttonsVisible = this.hasCustomer && this.hasSurrogate;
          } else if (response.status == 401) {
            this.$root.$emit("session_failed");
          }
        })
        .catch((error) => {
          return error;
        });
    },
    searchPhoneNumber() {
      return (this.surrogatetel.split(",")[1] || "").trim();
    },
  },
};
</script>

<style scoped>
.member-lookup {
  --ink: #20252b;
  --muted: #68727d;
  --line: #d9dee3;
  --surface: #ffffff;
  color: var(--ink);
  max-width: 1500px;
  padding: 0;
}

.lookup-header {
  align-items: stretch;
  background: #f3f5f7;
  border-left: 5px solid #3f51b5;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 22px 26px;
}

.lookup-header h2 {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 0;
  margin: 0 0 6px;
}

.lookup-header p {
  color: var(--muted);
  margin: 0;
}

.lookup-form {
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(280px, 1fr) auto;
  max-width: 760px;
  width: 100%;
}

.lookup-form .md-form {
  margin: 0;
}

.lookup-results {
  margin-top: 24px;
}

.status-message {
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  padding: 14px 18px;
}

.status-message--danger {
  background: #fff1f0;
  border: 1px solid #e8aaa5;
  color: #9f251d;
}

.result-grid {
  align-items: start;
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.result-panel {
  background: var(--surface);
  border: 1px solid var(--line);
  min-width: 0;
}

.result-panel > header {
  align-items: center;
  background: #303840;
  color: #fff;
  display: flex;
  font-size: 16px;
  font-weight: 700;
  gap: 10px;
  min-height: 50px;
  padding: 12px 16px;
}

.result-panel--passwords > header {
  background: #24574d;
}

.result-count {
  background: #fff;
  border-radius: 10px;
  color: #24574d;
  font-size: 12px;
  margin-left: auto;
  min-width: 24px;
  padding: 2px 7px;
  text-align: center;
}

dl {
  margin: 0;
}

dl > div {
  border-bottom: 1px solid #edf0f2;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(105px, 0.8fr) minmax(0, 1.2fr);
  padding: 11px 16px;
}

dl > div:last-child {
  border-bottom: 0;
}

dt {
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.empty-status {
  color: #9f251d;
  font-weight: 700;
}

.password-item + .password-item {
  border-top: 6px solid #edf0f2;
}

.password-code {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 14px 16px 8px;
}

.password-code strong {
  font-size: 20px;
  letter-spacing: 0;
}

.password-status {
  border-radius: 2px;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 8px;
}

.password-status--ok {
  background: #e3f3ec;
  color: #176348;
}

.password-status--danger {
  background: #fae5e3;
  color: #9f251d;
}

.empty-result {
  color: var(--muted);
  margin: 0;
  padding: 24px 16px;
}

.lookup-actions {
  border-top: 1px solid var(--line);
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, minmax(180px, 1fr));
  margin-top: 24px;
  padding-top: 16px;
}

@media (max-width: 1050px) {
  .result-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .lookup-actions {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }

  .result-panel--passwords {
    grid-column: 1 / -1;
  }
}

@media (max-width: 720px) {
  .lookup-form,
  .result-grid {
    grid-template-columns: 1fr;
  }

  .result-panel--passwords {
    grid-column: auto;
  }

  .lookup-actions {
    grid-template-columns: 1fr;
  }
}
</style>
